import { FC, useEffect, useRef, useState } from 'react';
import { Popover, PopoverBody, PopoverTrigger, useOutsideClick, theme } from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import { DateTime } from 'luxon';
import { CbSelect } from '../CbSelect';
import { QuickFilterTooltip } from './QuickFilterTooltip';
import { NO_FILTER } from '../../../hooks/useTable/useTable';
interface DateRangePickerProps {
  componentProps?: Record<string, string>;
  onChange: ({
    value: string
  }) => void;
  tooltipContent: string | null;
  value: string;
}
export enum CannedDateRange {
  Custom = 'custom',
  Last7Days = 'last_7_days',
  LastMonth = 'last_month',
  LastYear = 'last_year',
  MonthToDate = 'month_to_date',
  ThisYear = 'this_year',
  Today = 'today',
}
type Option = {
  name: string;
  value: string;
};
export const STOCK_OPTIONS: Option[] = [{
  name: 'Today',
  value: CannedDateRange.Today
}, {
  name: 'Last 7 days',
  value: CannedDateRange.Last7Days
}, {
  name: 'Month to date',
  value: CannedDateRange.MonthToDate
}, {
  name: 'Last month',
  value: CannedDateRange.LastMonth
}, {
  name: 'Year to date',
  value: CannedDateRange.ThisYear
}, {
  name: 'Last year',
  value: CannedDateRange.LastYear
}, {
  name: 'All time',
  value: NO_FILTER
}, {
  name: 'Custom...',
  value: CannedDateRange.Custom
}];
export const DateRangePicker: FC<DateRangePickerProps> = ({
  componentProps = {},
  onChange,
  tooltipContent,
  value
}) => {
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [customDateOption, setCustomDateOption] = useState<Option>();
  const options = STOCK_OPTIONS.concat(customDateOption || []);
  const onUnfoundValue = () => {
    const unfoundOption = {
      name: value,
      value
    };
    setCustomDateOption(unfoundOption);
    return unfoundOption;
  };
  const selectedOption = options.find(opt => opt.value === value) || onUnfoundValue();
  const handleChange = (opt: Option) => {
    if (opt.value === CannedDateRange.Custom) {
      setPopoverOpen(true);
    } else {
      onChange(opt);
      setCustomDateOption(undefined);
    }
  };
  const handleRangeChange = ({
    startDate,
    endDate
  }: {
    startDate: DateTime;
    endDate: DateTime;
  }) => {
    const parsed = `${startDate.setLocale('en-US').toLocaleString(DateTime.DATE_SHORT)} ~ ${endDate.setLocale('en-US').toLocaleString(DateTime.DATE_SHORT)}`;
    const customOption = {
      name: parsed,
      value: parsed
    };
    setCustomDateOption(customOption);
    onChange(customOption);
    setPopoverOpen(false);
  };
  const onPickerBlur = () => setPopoverOpen(false);
  return <Popover isOpen={popoverOpen} placement="bottom" data-sentry-element="Popover" data-sentry-component="DateRangePicker" data-sentry-source-file="DateRangePicker.tsx">
      <PopoverTrigger data-sentry-element="PopoverTrigger" data-sentry-source-file="DateRangePicker.tsx">
        <QuickFilterTooltip content={tooltipContent} data-sentry-element="QuickFilterTooltip" data-sentry-source-file="DateRangePicker.tsx">
          <CbSelect disabled={popoverOpen} getValueFromOption={opt => opt.value} onChange={handleChange} options={options} value={selectedOption} {...componentProps} data-sentry-element="CbSelect" data-sentry-source-file="DateRangePicker.tsx" />
        </QuickFilterTooltip>
      </PopoverTrigger>
      <PopoverBody position="absolute" zIndex={theme.zIndices.popover} data-sentry-element="PopoverBody" data-sentry-source-file="DateRangePicker.tsx">
        {/* Conditionally render so that the picker leaves the DOM (and resets state) between uses */}
        {popoverOpen && <Picker onRangeSelected={handleRangeChange} onBlur={onPickerBlur} />}
      </PopoverBody>
    </Popover>;
};
function Picker({
  onBlur,
  onRangeSelected
}) {
  const [selectedDate, setSelectedDate] = useState<DateTime>();
  const [startDate, setStartDate] = useState<DateTime>();
  const [endDate, setEndDate] = useState<DateTime>();
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick({
    handler: onBlur,
    ref
  });
  useEffect(() => {
    if (!endDate) return;
    onRangeSelected({
      startDate,
      endDate
    });
  }, [endDate]);
  const onDatePickerChange = (dates: [Date, Date]) => {
    const [start, end] = dates.map(date => date && DateTime.fromJSDate(date));
    setStartDate(start);
    setSelectedDate(start);
    setEndDate(end);
  };
  return <div ref={ref} data-sentry-component="Picker" data-sentry-source-file="DateRangePicker.tsx">
      <DatePicker endDate={endDate?.toJSDate()} inline onChange={onDatePickerChange} selected={selectedDate?.toJSDate()} selectsRange startDate={startDate?.toJSDate()} data-sentry-element="DatePicker" data-sentry-source-file="DateRangePicker.tsx" />
    </div>;
}